/**
 * Shared types for the BookieBurglar Bet Agent — a browser-extension
 * driven screen-control agent that navigates a sportsbook on the
 * user's behalf, signs in if needed, and either pre-fills the bet
 * slip or fully submits the bet.
 *
 * The contract here is the wire format between the bet-juicer React
 * app (this codebase) and the external `bet-agent-extension` (a
 * separate MV3 Chrome extension). It MUST be kept stable across both
 * codebases — bumping the schema requires a coordinated extension
 * release, so try to extend additively (new optional fields) rather
 * than rename existing ones.
 *
 * Why a new module instead of extending `direct-betting/types.ts`:
 * the existing direct-betting flow drives a real exchange API
 * (Kalshi) where we hold credentials server-side and the user's
 * browser never leaves bet-juicer. The agent flow is the opposite —
 * credentials never leave the user's machine, and the user's browser
 * physically navigates to a third-party sportsbook. The trust
 * boundary is fundamentally different, so the surface is
 * intentionally separate.
 */

/* ─── BetIntent ─── */

/** What the agent is being asked to do. */
export type AgentMode =
  /** Just open the deep-link URL and stop. Safest. Default for first-time users. */
  | "navigate"
  /** Open URL, find the market, click into the slip, set stake — but do NOT submit. */
  | "prefill"
  /** All of the above PLUS click "Place Bet". Requires {@code consent.autoplace}. */
  | "autoplace";

/**
 * Per-call consent flags. The agent extension MUST refuse to
 * continue if the requested {@link AgentMode} requires a consent
 * the caller hasn't checked. Two locks in series so a stray UI bug
 * can't auto-bet.
 */
export interface AgentConsent {
  /** User has agreed the extension may use their stored credentials for this call. */
  creds: boolean;
  /** User has agreed the extension may click "Place Bet" without further confirmation. */
  autoplace: boolean;
}

/**
 * Identifying info about a single selection on a sportsbook. The
 * extension's per-book adapter uses this to disambiguate a market
 * row in the DOM. Sid alone is not always enough — sportsbooks
 * don't expose The Odds API's sid, so we always include event +
 * market + line/point and let the adapter reconcile.
 */
export interface BetIntentSelection {
  /** Free-form pick label as shown on bet-juicer, e.g. "Nikola Jokic Over 29.5". */
  name: string;
  /** Optional numeric line / point — used for tolerance-bounded matching in the slip. */
  point?: number;
  /** Listed American odds at the moment of intent emission. */
  price: number;
}

/** A single leg the agent should attempt to add to the slip. */
export interface BetIntentLeg {
  /** Bookmaker key matching {@code bet-finder-service.ts} registry, e.g. "draftkings". */
  bookmakerKey: string;
  /** League key, e.g. "basketball_nba". Adapter uses this for URL routing. */
  sport: string;
  /** Sportsbook-native event id when known (rare — usually empty). */
  eventId?: string;
  /** Display team names. Used by adapter to fuzzy-match the event card. */
  homeTeam: string;
  awayTeam: string;
  /** Event start time in ISO-8601 UTC. Helps disambiguate doubleheaders. */
  commenceTimeIso: string;
  /** Market key matching The Odds API conventions, e.g. "h2h" / "spreads" / "player_points". */
  marketKey: string;
  selection: BetIntentSelection;
  /**
   * Pre-resolved deep-link URL when bet-juicer has one (positive-EV
   * rows often carry an `outcomeLink`). The adapter SHOULD prefer
   * this URL over its own search heuristic; if absent, the adapter
   * falls back to its own URL builder.
   */
  deepLink?: string | null;
  /**
   * Market-level fallback link (this book's market page). Used by the
   * deep-link builder when {@link #deepLink} (the outcome link) is
   * absent. The fallback chain is outcome → market → event → homepage.
   */
  marketLink?: string | null;
  /** Event-level fallback link (the game page). Third rung of the fallback chain. */
  eventLink?: string | null;
}

/**
 * The wire payload sent from bet-juicer to the bet-agent extension.
 *
 * Single-leg bets are just {@code legs.length === 1}. Parlays are
 * a single intent with N legs — the orchestrator opens one
 * sportsbook tab and feeds the legs to the adapter sequentially.
 * Cross-book parlays are NOT supported (a parlay must be on one
 * book to be a parlay) — the React side enforces this.
 */
export interface BetIntent {
  /**
   * Wire schema version. Increment when the shape changes in a
   * non-additive way. Extensions MUST refuse intents with a higher
   * version than they understand.
   */
  schemaVersion: 1;
  /** Stable client-generated id so the React UI can correlate state events back to a row. */
  intentId: string;
  /**
   * For parlays, all legs MUST share {@code bookmakerKey} (parlays
   * are single-book). For straight bets, exactly one leg.
   */
  legs: BetIntentLeg[];
  /** Optional pre-fill stake in USD. Required when {@code mode === 'autoplace'}. */
  stakeUsd?: number;
  mode: AgentMode;
  consent: AgentConsent;
  /**
   * Origin tag used by the extension's per-book "trust the agent"
   * toggle and by analytics. Free-form, e.g. "parlay-builder",
   * "positive-ev", "arbitrage".
   */
  source?: string;
}

/* ─── State machine events (extension → React) ─── */

/**
 * Lifecycle states for a single intent. Mirrors the orchestrator
 * state machine in the extension service worker. The React app
 * uses these to render a sticky status drawer.
 */
export type AgentState =
  | "queued"
  | "opening_tab"
  | "tab_ready"
  | "checking_auth"
  | "needs_login"
  | "prompting_creds"
  | "logging_in"
  /**
   * Hybrid-auth fallback: the extension parked the run because
   * automated form-filling failed (or no creds were stored) and is
   * waiting for the human to finish signing in via the sportsbook
   * tab. The user resumes the run by clicking "Continue" in the
   * extension's toolbar popup.
   */
  | "awaiting_manual_auth"
  | "finding_market"
  | "market_found"
  | "adding_to_slip"
  | "slip_verified"
  | "confirming_stake"
  | "stake_set"
  | "submitting"
  | "done_navigated"
  | "done_prefilled"
  | "done_placed"
  | "aborted"
  | "error";

export interface AgentEvent {
  intentId: string;
  state: AgentState;
  /** Index of the leg currently being processed in a parlay. 0 for straight bets. */
  legIndex?: number;
  /** Human-readable status message for the toast. */
  message?: string;
  /** Error reason when {@code state === 'error'}. */
  reason?: string;
  /** Sportsbook-side ticket id when {@code state === 'done_placed'}. */
  ticketId?: string;
  /** Monotonic timestamp (ms since epoch) for ordering. */
  ts: number;
}

/* ─── Helpers ─── */

/** Crypto-strong intent id. Doesn't need to be a uuid; just unique enough. */
export function newIntentId(): string {
  // crypto.randomUUID is widely available in modern browsers; fall
  // back to a math-random-derived id for older browsers / SSR.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `intent_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

/**
 * The set of states that mean the agent is finished (success or
 * failure) and the toast should auto-dismiss after a few seconds.
 */
export const TERMINAL_STATES: ReadonlySet<AgentState> = new Set<AgentState>([
  "done_navigated",
  "done_prefilled",
  "done_placed",
  "aborted",
  "error",
]);

/** True when the intent is in a "needs user attention" state. */
export const BLOCKING_STATES: ReadonlySet<AgentState> = new Set<AgentState>([
  "needs_login",
  "prompting_creds",
  "awaiting_manual_auth",
  "confirming_stake",
]);
